import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// ---------------------------------------------------------------------------
// Merkliste als EINE Quelle für die ganze Anwendung.
//
// Vorher hielt jede Karte ihren Merk-Zustand selbst — und zwar immer als
// „nicht gemerkt", weil niemand die Liste abrief. Auf der Suchseite und der
// Startseite war das Herz deshalb auch bei gemerkten Objekten leer, und ein
// Klick darauf schaltete um, LÖSCHTE den Eintrag also und meldete „Aus
// Favoriten entfernt". Der einzige Weg, das dauerhaft zu verhindern, ist eine
// gemeinsame Quelle: Wer die IDs kennt, kann sie nicht falsch anzeigen.
//
// Die Liste wird einmal je Anmeldung geholt und danach nur noch lokal
// fortgeschrieben (der Server liefert bei jedem Umschalten den neuen Zustand
// zurück). Ohne Anmeldung bleibt sie leer — Merken erfordert ein Konto.
// ---------------------------------------------------------------------------
const FavoritesContext = createContext(null);

export function FavoritesProvider({ children }) {
  const { user } = useAuth();
  const [ids, setIds] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  // Verhindert, dass eine spät eintreffende Antwort eine inzwischen aktuellere
  // lokale Änderung überschreibt (Nutzer klickt, während die Liste noch lädt).
  const laufNr = useRef(0);

  useEffect(() => {
    // user: null = wird geprüft, false = anonym, Objekt = angemeldet
    if (!user) {
      setIds(new Set());
      return;
    }
    const meine = ++laufNr.current;
    setLoading(true);
    api.get("/favorites")
      .then(({ data }) => {
        if (laufNr.current !== meine) return;
        const liste = Array.isArray(data) ? data : data?.items || [];
        setIds(new Set(liste.map((p) => p?.id ?? p?.property_id).filter(Boolean)));
      })
      .catch(() => { /* Merkliste ist nicht kritisch — im Zweifel leer lassen */ })
      .finally(() => { if (laufNr.current === meine) setLoading(false); });
  }, [user]);

  const isFav = useCallback((id) => ids.has(id), [ids]);

  // Gibt den neuen Zustand zurück, damit der Aufrufer die passende Meldung
  // zeigen kann. Wirft weiter, wenn der Server ablehnt — dann bleibt die
  // Anzeige unverändert, statt einen Zustand vorzutäuschen.
  const toggle = useCallback(async (id) => {
    const { data } = await api.post("/favorites/toggle", { property_id: id });
    const gemerkt = !!data?.favorited;
    setIds((alt) => {
      const neu = new Set(alt);
      if (gemerkt) neu.add(id); else neu.delete(id);
      return neu;
    });
    return gemerkt;
  }, []);

  const wert = useMemo(() => ({ ids, isFav, toggle, loading, count: ids.size }), [ids, isFav, toggle, loading]);
  return <FavoritesContext.Provider value={wert}>{children}</FavoritesContext.Provider>;
}

export function useFavorites() {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error("useFavorites braucht den FavoritesProvider");
  return ctx;
}
